import { useNavigate, useSearch } from "@tanstack/react-router"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { IconButton } from "@holigral/shared/ui/composed"
import { useT } from "@/i18n"

type Props = {
  readonly total: number
  readonly perPage: number
}

/**
 * Pagination for any list route that keeps its current page in the
 * `page` search param. It reads and writes that param directly, so the
 * page survives reload and is shareable by URL; the embedding page only
 * supplies the totals.
 */
export default function TablePagination({ total, perPage }: Props) {
  const { t } = useT("common")
  const navigate = useNavigate()
  const search = useSearch({ strict: false })
  const page = search.page ?? 1
  const pages = Math.max(1, Math.ceil(total / perPage))

  if (pages <= 1) {
    return null
  }

  const goTo = (next: number) => {
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, page: next }),
      replace: true,
    })
  }

  return (
    <div className="flex items-center justify-end gap-2">
      <span className="text-sm text-muted-foreground">
        {t("pagination.pageOf", { page, pages })}
      </span>
      <IconButton
        icon={<ChevronLeft />}
        variant="outline"
        size="icon-sm"
        disabled={page <= 1}
        aria-label={t("pagination.previous")}
        onClick={() => {
          goTo(page - 1)
        }}
      />
      <IconButton
        icon={<ChevronRight />}
        variant="outline"
        size="icon-sm"
        disabled={page >= pages}
        aria-label={t("pagination.next")}
        onClick={() => {
          goTo(page + 1)
        }}
      />
    </div>
  )
}
